// react
import { useState, useEffect } from 'react';

// mui
import {
    Grid,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Autocomplete,
    TextField,
    Typography,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// components
import { ListboxComponent, MarkDown, ContainerScroll } from '../internal.js';
import { DotLoading } from '../animation/dot-loading.jsx';

import { storage } from '@goldblack/client-storage';

const optionsReportPost = [
    { label: 'Harassment or bullying', code: 'harassment' },
    { label: 'Sexual or suggestive content', code: 'sexual' },
    { label: 'Hate speech', code: 'hate' },
    { label: 'Threats or violence', code: 'threats' },
    { label: 'Scam or spam', code: 'spam' },
    { label: 'Self-harm', code: 'self_harm' },
    { label: 'Something else', code: 'other' },
];

const optionsReportUser = [
    { label: 'Spam account', code: 'spam_account' },
    { label: 'Impersonation', code: 'impersonation' },
    { label: 'Persistent harassment', code: 'harassment' },
    { label: 'Bot', code: 'bot' },
    { label: 'Malicious user', code: 'malicious' },
];

const optionsReportComment = [
    { label: 'Harassment or bullying', code: 'harassment' },
    { label: 'Sexual or suggestive content', code: 'sexual' },
    { label: 'Hate speech', code: 'hate' },
    { label: 'Threats or violence', code: 'threats' },
    { label: 'Scam or spam', code: 'spam' },
    { label: 'Self-harm', code: 'self_harm' },
    { label: 'Something else', code: 'other' },
];

const optionsReportMessage = [
    { label: 'Harassment or bullying', code: 'harassment' },
    { label: 'Sexual or suggestive content', code: 'sexual' },
    { label: 'Hate speech', code: 'hate' },
    { label: 'Threats or violence', code: 'threats' },
    { label: 'Scam or spam', code: 'spam' },
    { label: 'Self-harm', code: 'self_harm' },
    { label: 'Something else', code: 'other' },
];

// Small confirm dialogs shrink to their text, so short messages made a tiny box. The cap keeps it inside
// narrow screens (the Dialog paper has a 32px margin on each side).
const DIALOG_MIN_WIDTH = 'min(300px, calc(100vw - 64px))';

function CommonOkDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                    sx: { minWidth: DIALOG_MIN_WIDTH },
                },
            }}>
            <DialogTitle variant='m_dialog_title'>{props.title}</DialogTitle>
            <DialogContent>
                <DialogContentText variant='m_dialog_content_text'>{props.desc}</DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button variant='contained' onClick={props.onOk} autoFocus>
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function CommonYesNoDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                    sx: { minWidth: DIALOG_MIN_WIDTH },
                },
            }}>
            <DialogTitle variant='m_dialog_title'>{props.title}</DialogTitle>
            <DialogContent>
                <DialogContentText variant='m_dialog_content_text'>{props.desc}</DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button variant='outlined' onClick={props.onNo} autoFocus>
                    No
                </Button>
                <Button variant='contained' onClick={props.onYes}>
                    Yes
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ReportDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                    sx: { minWidth: 380 },
                },
            }}>
            <DialogTitle variant='m_dialog_title'>{props.title}</DialogTitle>
            <DialogContent>
                <DialogContentText variant='m_dialog_content_text'>{props.desc}</DialogContentText>
                <Autocomplete
                    sx={{ my: 3 }}
                    options={
                        props.type === 'post'
                            ? optionsReportPost
                            : props.type === 'comment'
                              ? optionsReportComment
                              : props.type === 'message'
                                ? optionsReportMessage
                                : optionsReportUser
                    }
                    getOptionLabel={(option) => option.label}
                    slotProps={{
                        listbox: {
                            component: ListboxComponent,
                        },
                        paper: {
                            variant: 'outlined',
                        },
                    }}
                    renderOption={(props, option) => (
                        <Box component='li' {...props}>
                            <Typography sx={{ fontSize: '14px' }}>{option.label}</Typography>
                        </Box>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label='reason'
                            variant='standard'
                            slotProps={{
                                htmlInput: {
                                    ...params.inputProps, // 반드시 포함 (Autocomplete의 핵심 로직)
                                    style: {
                                        fontSize: '16px', // 1. 브라우저는 16px로 인식해서 줌을 안 함
                                        transform: 'scale(0.875)', // 2. 실제 크기를 14px 정도로 축소 (14/16 = 0.875)
                                        transformOrigin: 'left center', // 3. 왼쪽 정렬 유지
                                        width: '114.2%', // 4. 축소된 만큼 가로 길이를 보정 (100 / 0.875)
                                    },
                                },
                                inputLabel: {
                                    style: { fontSize: '14px' },
                                    sx: {
                                        '&.MuiInputLabel-shrink': {
                                            fontSize: '11px !important', // 2. 입력 후 라벨을 아주 작게 해서
                                            transform: 'translate(0, -2px) scale(1)', // 3. 위로 꽉 붙임
                                        },
                                    },
                                },
                                input: {
                                    ...params.InputProps,
                                },
                            }}
                        />
                    )}
                    onChange={props.onChange}
                />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button variant='outlined' onClick={props.onCancel} autoFocus>
                    Cancel
                </Button>
                <Button variant='contained' disabled={props.okDisabled} onClick={props.onOK}>
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function BlockedDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle variant='m_dialog_title' sx={{ color: 'primary.main' }}>
                Your account is blocked
            </DialogTitle>
            <DialogContent>
                <DialogContentText variant='m_dialog_content_text' sx={{ color: 'secondary.main' }}>
                    This block will expire on {props.expireDate}. Tell us if you think we made a mistake.
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button variant='contained' disabled={props.okDisabled} onClick={props.onOK}>
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function InactiveDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle variant='m_dialog_title' sx={{ color: 'primary.main' }}>
                Your account is inactive
            </DialogTitle>
            <DialogContent>
                <DialogContentText variant='m_dialog_content_text' sx={{ color: 'secondary.main' }}>
                    Your account has been forcibly inactive by administrator. Tell us if you think we made a mistake.
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button variant='contained' disabled={props.okDisabled} onClick={props.onOK}>
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ConsentDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle variant='m_dialog_title' sx={{ color: 'primary.main' }}>
                Sensitive Information
            </DialogTitle>
            <DialogContent>
                <DialogContentText variant='m_dialog_content_text' sx={{ color: 'secondary.main' }}>
                    This information is considered sensitive and completely optional. If you choose to provide it, it
                    may appear on your profile and be used for matching or filtering. You can change or remove it
                    anytime.
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button variant='outlined' onClick={props.onCancel} autoFocus>
                    Cancel
                </Button>
                <Button variant='contained' onClick={props.onOK}>
                    Agree
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function LegalDialog({ open, isTopmost, title, type, url, onAgree, onCancel, readOnly, onClose }) {
    const [content, setContent] = useState('');
    const [meta, setMeta] = useState(null); // { version, effectiveAt? } from the legal json
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (open && url) {
            setLoading(true);

            fetch(url)
                .then((response) => {
                    if (!response.ok) throw new Error('failed to load content');
                    return response.json();
                })
                .then((doc) => {
                    setContent(doc.data);
                    setMeta(doc.meta);
                    setLoading(false);
                })
                .catch((err) => {
                    storage.transience.setState('internal.error', err.message);
                    setLoading(false);
                });
        }
    }, [open, url]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (readOnly) {
                onClose?.();
                return;
            }
            if (!isTopmost) return;
            e.stopImmediatePropagation();
            // A notice has only "Ok"; Escape must not open the exit (logout) dialog.
            if (type === 'privacy' || type === 'terms') onCancel?.();
        };
        if (open) {
            window.addEventListener('keydown', handleKeyDown, true);
        }
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [open, isTopmost, onCancel, readOnly, onClose, type]);

    return (
        <Dialog
            open={open}
            fullScreen
            slotProps={{
                paper: {
                    elevation: 0,
                    sx: {
                        backgroundColor: 'background.default',
                    },
                },
            }}>
            <DialogTitle
                sx={{
                    color: 'primary.main',
                    textAlign: 'center',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}>
                <Grid container spacing={0} direction='column' justifyContent='center' alignItems='center'>
                    <Typography variant='m_titleL' color='primary.main' sx={{ m: 2 }}>
                        {title}
                    </Typography>
                    {(type === 'privacy' || type === 'terms') && meta ? (
                        <>
                            <Typography
                                align='center'
                                variant='m_overline'
                                color='primary.main'
                                sx={{ fontSize: '0.65rem' }}>
                                version {meta.version}
                            </Typography>
                            {meta.effectiveAt && (
                                <Typography
                                    align='center'
                                    variant='m_overline'
                                    color='primary.main'
                                    sx={{ fontSize: '0.65rem' }}>
                                    effective{' '}
                                    {new Date(meta.effectiveAt).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        timeZone: 'UTC',
                                    })}
                                </Typography>
                            )}
                        </>
                    ) : null}
                </Grid>
                {readOnly && (
                    <IconButton onClick={onClose} size='small' sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon fontSize='small' />
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {loading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flex: 1,
                            minHeight: '200px',
                        }}>
                        <DotLoading variant='pulse' />
                    </Box>
                ) : (
                    <ContainerScroll sx={{ flex: 1, px: 2, py: 1 }}>
                        <MarkDown src={content} />
                    </ContainerScroll>
                )}
            </DialogContent>
            {!readOnly && (
                <Box
                    sx={{
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        p: 2,
                        backgroundColor: 'background.paper',
                    }}>
                    {type === 'privacy' || type === 'terms' ? (
                        <>
                            <Typography variant='body2' sx={{ textAlign: 'center', mb: 2, color: 'text.secondary' }}>
                                I have read and understood the {title}, and I agree to its terms
                            </Typography>
                            <DialogActions sx={{ justifyContent: 'center', p: 0 }}>
                                <Button variant='outlined' onClick={onCancel}>
                                    Cancel
                                </Button>
                                <Button variant='contained' onClick={onAgree} disabled={loading}>
                                    Agree
                                </Button>
                            </DialogActions>
                        </>
                    ) : (
                        <DialogActions sx={{ justifyContent: 'center', p: 0 }}>
                            <Button variant='contained' onClick={onAgree} disabled={loading}>
                                Ok
                            </Button>
                        </DialogActions>
                    )}
                </Box>
            )}
        </Dialog>
    );
}

function ExitOptionDialog(props) {
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (!props.isTopmost) return;
            e.stopImmediatePropagation();
            props.onCancel?.();
        };
        if (props.open) {
            window.addEventListener('keydown', handleKeyDown, true);
        }
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [props.open, props.isTopmost, props.onCancel]);

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle variant='m_dialog_title' sx={{ color: 'primary.main', textAlign: 'center' }}>
                Agreement Required
            </DialogTitle>
            <DialogContent>
                <DialogContentText variant='m_dialog_content_text'>
                    You must agree to the updated terms to continue using Loise. If you do not agree, you may sign out
                    for now or permanently delete your account.
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button variant='outlined' onClick={props.onCancel}>
                    Cancel
                </Button>
                <Button variant='outlined' onClick={props.onLogout} autoFocus>
                    Logout
                </Button>
                <Button variant='contained' color='error' onClick={props.onDeleteAccount}>
                    Delete Account
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function DeleteAccountDialog(props) {
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (!props.isTopmost) return;
            e.stopImmediatePropagation();
            props.onCancel?.();
        };
        if (props.open) {
            window.addEventListener('keydown', handleKeyDown, true);
        }
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [props.open, props.isTopmost, props.onCancel]);

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle variant='m_dialog_title' sx={{ color: 'primary.main', textAlign: 'center' }}>
                Delete Account
            </DialogTitle>
            <DialogContent>
                <DialogContentText variant='m_dialog_content_text'>
                    Your account and all associated data will be deleted immediately. This action is permanent and
                    cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button variant='outlined' onClick={props.onCancel}>
                    Cancel
                </Button>
                <Button variant='contained' color='error' onClick={props.onContinue}>
                    Continue
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function DeleteAccountConfirmDialog(props) {
    const [value, setValue] = useState('');
    const confirmed = value.trim() === 'DELETE';

    const handleClose = () => {
        setValue('');
        props.onClose?.();
    };

    const handleConfirm = () => {
        if (!confirmed) return;
        setValue('');
        props.onConfirm?.();
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (!props.isTopmost) return;
            e.stopImmediatePropagation();
            handleClose();
        };
        if (props.open) {
            window.addEventListener('keydown', handleKeyDown, true);
        }
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [props.open, props.isTopmost]);

    return (
        <Dialog
            open={props.open}
            onClose={handleClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle variant='m_dialog_title' sx={{ color: 'primary.main' }}>
                Delete Account
            </DialogTitle>
            <DialogContent>
                <DialogContentText variant='m_dialog_content_text' sx={{ mb: 2 }}>
                    To confirm, please type DELETE in the box below.
                </DialogContentText>
                <TextField
                    fullWidth
                    placeholder='DELETE'
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    autoComplete='off'
                    slotProps={{ htmlInput: { 'aria-label': 'Type DELETE to confirm' } }}
                />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button variant='outlined' onClick={handleClose} autoFocus>
                    Cancel
                </Button>
                <Button variant='contained' color='error' disabled={!confirmed} onClick={handleConfirm}>
                    Delete My Account
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function JoinServiceDialog(props) {
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (!props.isTopmost) return;
            e.stopImmediatePropagation();
            props.onCancel?.();
        };
        if (props.open) {
            window.addEventListener('keydown', handleKeyDown, true);
        }
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [props.open, props.isTopmost, props.onCancel]);

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle variant='m_dialog_title' sx={{ color: 'primary.main', textAlign: 'center' }}>
                Join Service
            </DialogTitle>
            <DialogContent>
                <DialogContentText variant='m_dialog_content_text'>
                    Would you like to start using {props.serviceName}?
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button variant='outlined' onClick={props.onCancel}>
                    No
                </Button>
                <Button variant='contained' onClick={props.onContinue}>
                    Yes
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function LeaveServiceDialog(props) {
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (!props.isTopmost) return;
            e.stopImmediatePropagation();
            props.onCancel?.();
        };
        if (props.open) {
            window.addEventListener('keydown', handleKeyDown, true);
        }
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [props.open, props.isTopmost, props.onCancel]);

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle variant='m_dialog_title' sx={{ color: 'primary.main', textAlign: 'center' }}>
                Leave Service
            </DialogTitle>
            <DialogContent>
                <DialogContentText variant='m_dialog_content_text'>
                    All data associated with the service will be deleted immediately. This action is permanent and
                    cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button variant='outlined' onClick={props.onCancel}>
                    Cancel
                </Button>
                <Button variant='contained' color='error' onClick={props.onContinue}>
                    Continue
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function LeaveServiceConfirmDialog(props) {
    const [value, setValue] = useState('');
    const confirmed = value.trim() === 'LEAVE';

    const handleClose = () => {
        setValue('');
        props.onClose?.();
    };

    const handleConfirm = () => {
        if (!confirmed) return;
        setValue('');
        props.onConfirm?.();
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (!props.isTopmost) return;
            e.stopImmediatePropagation();
            handleClose();
        };
        if (props.open) {
            window.addEventListener('keydown', handleKeyDown, true);
        }
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [props.open, props.isTopmost]);

    return (
        <Dialog
            open={props.open}
            onClose={handleClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle variant='m_dialog_title' sx={{ color: 'primary.main' }}>
                Leave Service
            </DialogTitle>
            <DialogContent>
                <DialogContentText variant='m_dialog_content_text' sx={{ mb: 2 }}>
                    To confirm, please type LEAVE in the box below.
                </DialogContentText>
                <TextField
                    fullWidth
                    placeholder='LEAVE'
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    autoComplete='off'
                    slotProps={{ htmlInput: { 'aria-label': 'Type LEAVE to confirm' } }}
                />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button variant='outlined' onClick={handleClose} autoFocus>
                    Cancel
                </Button>
                <Button variant='contained' color='error' disabled={!confirmed} onClick={handleConfirm}>
                    Leave Service
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ImageDialog(props) {
    // The image fills the dialog and a tap anywhere closes it (no button).
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            maxWidth='sm'
            fullWidth
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                    sx: { m: 2, maxHeight: 'calc(100% - 32px)' },
                },
            }}>
            <DialogContent
                onClick={props.onClose}
                sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
                {props.src ? (
                    <img
                        src={props.src}
                        alt='Profile image'
                        style={{
                            display: 'block',
                            width: '100%',
                            height: 'auto',
                            maxHeight: 'calc(100vh - 34px)',
                            objectFit: 'contain',
                        }}
                    />
                ) : (
                    <Typography color='text.secondary' sx={{ p: 4 }}>
                        Image unavailable
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
}

export {
    CommonOkDialog,
    CommonYesNoDialog,
    ReportDialog,
    BlockedDialog,
    InactiveDialog,
    ConsentDialog,
    LegalDialog,
    ExitOptionDialog,
    DeleteAccountDialog,
    DeleteAccountConfirmDialog,
    LeaveServiceDialog,
    LeaveServiceConfirmDialog,
    JoinServiceDialog,
    ImageDialog,
};
